import React from 'react';
import { Modal, Button } from 'antd';
import { ExclamationCircleFilled } from '@ant-design/icons';
import { UserDisplay } from '../../../../pages/users/types/UserDisplay';
import { ContactsTransferAnswer, ContactsTransferQuestion, getContactsTransferChoices } from '../../../../hooks/subscription/useNumberAssignmentSteps';
import ContactsTransferText from '../../../@shared/number-assignment/ContactsTransferText';
import NumberAssignmentProgress from '../../../@shared/number-assignment/NumberAssignmentProgress';
import './DeactivateUserModal.css';

interface DeactivateUserModalProps {
  isOpen: boolean;
  user: UserDisplay | null;
  isLoading?: boolean;
  /** Asked once confirmed, when contacts are on the number: keep them on it or move them with their owner */
  transferQuestion?: ContactsTransferQuestion | null;
  /** The backend is freeing the number and moving its contacts */
  isAssigning?: boolean;
  onCancel: () => void;
  onConfirm: () => void;
  onAnswerTransfer: (transfer: ContactsTransferAnswer) => void;
}

const DeactivateUserModal: React.FC<DeactivateUserModalProps> = ({
  isOpen,
  user,
  isLoading = false,
  transferQuestion = null,
  isAssigning = false,
  onCancel,
  onConfirm,
  onAnswerTransfer,
}) => {
  if (!user) return null;

  // Once the question is answered, the modal stays open until the number is freed. Before, closing it cancels: nothing is written
  const isLocked = isAssigning;
  const transferChoices = transferQuestion ? getContactsTransferChoices(transferQuestion) : null;

  return (
    <Modal
      open={isOpen}
      onCancel={onCancel}
      footer={null}
      centered
      width={612}
      className="deactivate-user-modal"
      closable={!isLocked}
      maskClosable={!isLocked}
      keyboard={!isLocked}
    >
      <div className="deactivate-user-modal__content">
        <div className="deactivate-user-modal__header">
          <ExclamationCircleFilled className="deactivate-user-modal__icon" />
          <h3 className="deactivate-user-modal__title">Désassigner l'utilisateur ?</h3>
        </div>

        {isAssigning ? (
          <NumberAssignmentProgress />
        ) : transferChoices && transferQuestion ? (
          <>
            <p className="deactivate-user-modal__description">
              <ContactsTransferText question={transferQuestion} />
            </p>

            <div className="deactivate-user-modal__actions">
              <Button className="deactivate-user-modal__cancel-btn" onClick={() => onAnswerTransfer(transferChoices.secondary.answer)}>
                {transferChoices.secondary.label}
              </Button>
              <Button type="primary" className="deactivate-user-modal__confirm-btn" onClick={() => onAnswerTransfer(transferChoices.primary.answer)}>
                {transferChoices.primary.label}
              </Button>
            </div>
          </>
        ) : (
          <>
            <p className="deactivate-user-modal__description">
              <strong>{user.name}</strong> sera désassigné de son numéro.
            </p>

            <div className="deactivate-user-modal__actions">
              <Button className="deactivate-user-modal__cancel-btn" onClick={onCancel}>
                Annuler
              </Button>
              <Button
                type="primary"
                className="deactivate-user-modal__confirm-btn"
                onClick={onConfirm}
                loading={isLoading}
              >
                Désassigner l'utilisateur
              </Button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

export default DeactivateUserModal;
